import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Check, X, Trash2, Clock, Mail, MapPin, Instagram, Facebook, Music2, Quote, Inbox } from "lucide-react";

const STATUS_CFG = {
  pending:  { label: "Pending",  cls: "border-amber-700 bg-amber-950/40 text-amber-300", icon: Clock },
  approved: { label: "Approved", cls: "border-emerald-700 bg-emerald-950/40 text-emerald-300", icon: Check },
  rejected: { label: "Rejected", cls: "border-zinc-700 bg-zinc-900 text-zinc-400", icon: X },
};

function StatusPill({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.pending;
  const Icon = cfg.icon;
  return (
    <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 border inline-flex items-center gap-1 ${cfg.cls}`}>
      <Icon className="h-3 w-3" /> {cfg.label}
    </span>
  );
}

function RejectDialog({ open, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0F0F0F] border-[#27272A] text-white" data-testid="reject-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl">Reject story</DialogTitle>
          <DialogDescription className="text-zinc-500">Optional message sent to the author with their rejection email.</DialogDescription>
        </DialogHeader>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Thanks for sharing — but we need a longer story before we can publish."
          rows={4}
          className="bg-[#050505] border-[#27272A] rounded-sm"
          data-testid="reject-reason"
        />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="bg-[#050505] border-[#27272A] text-zinc-300 rounded-sm">Cancel</Button>
          <Button
            onClick={async () => { setBusy(true); await onConfirm(reason); setBusy(false); setReason(""); }}
            disabled={busy}
            className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm"
            data-testid="reject-confirm"
          >
            {busy ? "Rejecting…" : "Reject + Email Author"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StoryRow({ s, onChanged }) {
  const [rejecting, setRejecting] = useState(false);
  const [busy, setBusy] = useState(false);

  const setStatus = async (status, rejection_reason = null) => {
    setBusy(true);
    try {
      await api.patch(`/admin/stories/${s.id}`, { status, rejection_reason });
      toast.success(status === "approved" ? "Approved + author notified" : status === "rejected" ? "Rejected" : "Set to pending");
      await onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this story permanently? Photo will be deleted too.")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/stories/${s.id}`);
      toast.success("Deleted");
      await onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-[#27272A] bg-[#0F0F0F]" data-testid={`story-row-${s.id}`}>
      <div className="p-5 border-b border-[#27272A] flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <StatusPill status={s.status} />
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold inline-flex items-center gap-1">
              <Mail className="h-3 w-3" /> {s.user_email}
            </div>
            {s.city && <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {s.city}</div>}
            <div className="text-[10px] uppercase tracking-widest text-zinc-600">{new Date(s.created_at).toLocaleString()}</div>
          </div>
          <div className="font-bold text-white text-lg" data-testid={`story-author-${s.id}`}>{s.user_name}</div>
        </div>
        <div className="flex items-center gap-2">
          {s.status !== "approved" && (
            <Button onClick={() => setStatus("approved")} disabled={busy} className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold uppercase tracking-widest text-xs rounded-sm" data-testid={`story-approve-${s.id}`}>
              <Check className="h-3 w-3 mr-1" /> Approve
            </Button>
          )}
          {s.status !== "rejected" && (
            <Button onClick={() => setRejecting(true)} disabled={busy} variant="outline" className="bg-[#050505] border-[#27272A] text-zinc-300 hover:text-white rounded-sm text-xs uppercase tracking-widest font-bold" data-testid={`story-reject-${s.id}`}>
              <X className="h-3 w-3 mr-1" /> Reject
            </Button>
          )}
          {s.status !== "pending" && (
            <Button onClick={() => setStatus("pending")} disabled={busy} variant="outline" className="bg-[#050505] border-[#27272A] text-zinc-300 hover:text-white rounded-sm text-xs uppercase tracking-widest font-bold" data-testid={`story-unpublish-${s.id}`}>
              ↻ Pending
            </Button>
          )}
          <Button onClick={remove} disabled={busy} variant="outline" className="bg-[#050505] border-[#27272A] text-red-400 hover:text-red-300 rounded-sm text-xs uppercase tracking-widest font-bold" data-testid={`story-delete-${s.id}`}>
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>

      <div className="p-5 grid md:grid-cols-3 gap-5">
        {s.image_url && (
          <div className="md:col-span-1">
            <img src={s.image_url} alt={s.headline} className="w-full aspect-[4/3] object-cover border border-[#27272A]" />
          </div>
        )}
        <div className={s.image_url ? "md:col-span-2" : "md:col-span-3"}>
          <h3 className="font-display uppercase text-xl tracking-tight mb-2 text-white">{s.headline}</h3>
          <p className="text-zinc-300 text-sm leading-relaxed whitespace-pre-wrap mb-3">{s.story}</p>
          <div className="border-l-2 border-[#FF331F] pl-3 my-3">
            <div className="flex items-start gap-2 text-zinc-200 text-sm italic">
              <Quote className="h-3 w-3 text-[#FF331F] flex-shrink-0 mt-1" />
              <p className="whitespace-pre-wrap">{s.moral}</p>
            </div>
          </div>

          {(s.social?.instagram || s.social?.tiktok || s.social?.facebook) && (
            <div className="flex items-center gap-2 flex-wrap text-xs text-zinc-400 mt-3">
              <span className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">Socials:</span>
              {s.social.instagram && <span className="inline-flex items-center gap-1 border border-[#27272A] px-2 py-0.5"><Instagram className="h-3 w-3" /> {s.social.instagram}</span>}
              {s.social.tiktok && <span className="inline-flex items-center gap-1 border border-[#27272A] px-2 py-0.5"><Music2 className="h-3 w-3" /> {s.social.tiktok}</span>}
              {s.social.facebook && <span className="inline-flex items-center gap-1 border border-[#27272A] px-2 py-0.5"><Facebook className="h-3 w-3" /> {s.social.facebook}</span>}
            </div>
          )}

          {s.status === "rejected" && s.rejection_reason && (
            <div className="mt-3 p-2 border border-zinc-700 bg-zinc-950 text-xs text-zinc-400 italic">
              <span className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mr-1">Rejection note:</span>{s.rejection_reason}
            </div>
          )}
        </div>
      </div>

      <RejectDialog open={rejecting} onClose={() => setRejecting(false)} onConfirm={async (reason) => { await setStatus("rejected", reason); setRejecting(false); }} />
    </div>
  );
}

export default function Stories() {
  const [data, setData] = useState({ stories: [], counts: { pending: 0, approved: 0, rejected: 0 } });
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/stories", { params: { status: filter } });
      setData(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="p-8 md:p-12" data-testid="stories-admin">
      <div className="mb-8 max-w-3xl">
        <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-2">Community</div>
        <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tight">Stories.</h1>
        <p className="text-zinc-400 text-sm mt-2">
          Review community story submissions. Approving instantly publishes the story and emails the author. Rejecting also emails them with the reason.
        </p>
      </div>

      {/* Counts */}
      <div className="grid grid-cols-3 gap-3 mb-8 max-w-2xl">
        <div className="border border-amber-700/50 bg-amber-950/20 p-3" data-testid="count-pending">
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold inline-flex items-center gap-1"><Clock className="h-3 w-3" /> Pending</div>
          <div className="text-2xl font-display mt-1 text-white">{data.counts.pending || 0}</div>
        </div>
        <div className="border border-emerald-700/50 bg-emerald-950/20 p-3" data-testid="count-approved">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold inline-flex items-center gap-1"><Check className="h-3 w-3" /> Approved</div>
          <div className="text-2xl font-display mt-1 text-white">{data.counts.approved || 0}</div>
        </div>
        <div className="border border-zinc-700 bg-zinc-900/50 p-3" data-testid="count-rejected">
          <div className="text-[10px] uppercase tracking-widest text-zinc-300 font-bold inline-flex items-center gap-1"><X className="h-3 w-3" /> Rejected</div>
          <div className="text-2xl font-display mt-1 text-white">{data.counts.rejected || 0}</div>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-2 mb-6 flex-wrap">
        {["pending", "approved", "rejected", "all"].map((s) => {
          const active = filter === s;
          const count = s === "all" ? Object.values(data.counts).reduce((a, b) => a + b, 0) : (data.counts[s] || 0);
          return (
            <button
              key={s}
              type="button"
              onClick={() => setFilter(s)}
              className={`text-xs uppercase tracking-widest font-bold px-3 py-2 border ${active ? "bg-[#FF331F] border-[#FF331F] text-white" : "bg-[#050505] border-[#27272A] text-zinc-400 hover:text-white"}`}
              data-testid={`filter-${s}`}
            >
              {s} ({count})
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm">Loading…</div>
      ) : data.stories.length === 0 ? (
        <div className="border border-dashed border-[#27272A] p-12 text-center" data-testid="stories-empty">
          <Inbox className="h-8 w-8 text-zinc-700 mx-auto mb-2" />
          <p className="text-zinc-400 text-sm">No stories in this view.</p>
        </div>
      ) : (
        <div className="space-y-4 max-w-5xl">
          {data.stories.map((s) => <StoryRow key={s.id} s={s} onChanged={load} />)}
        </div>
      )}
    </div>
  );
}
